/**
 * What is on the van, shipper by shipper, for the load it is about to inject.
 *
 * The arrivals board has always had this as a comma-joined list of names in a column two twelfths
 * wide. That is the right amount of detail for a desk with the whole table in front of it, and the
 * wrong amount for the phone a depot manager reads at 01:00 — which is why the message they type
 * into Slack by hand is a line per shipper with a quantity against each:
 *
 *     Exclusive Logistics - 1 cage
 *     TWS - 3 pallets
 *     Neom Wellbeing - 1 pallet
 *
 * So this pulls the same window of stops the names column already used, and keeps the figures
 * instead of throwing them away.
 *
 * Pure, and separate from the board, so the decisions worth holding still — which stops belong to
 * this injection, what a shipper is called when no customer is linked, whether a figure is the
 * driver's or the plan's — can be stated in a test.
 */
import { reportedStopVolume } from "@/lib/volumeReporting"

/** The stop fields this module reads, named as the database names them. */
export interface CollectionStopInput {
  id: string
  name: string
  stop_type: string
  status?: string
  volume_forecast?: number | null
  volume_actual?: number | null
  loading_units_forecast?: number | null
  loading_units_actual?: number | null
  loading_unit_type?: string | null
  /**
   * The joined location, whose customer linkage arrives under one of two keys depending on which
   * query fetched it — `location_customers` is the table, `customers` is what the type calls it.
   * Loosely typed because both spellings are real and the board has always had to try both.
   */
  location?: {
    site_name?: string | null
    location_customers?: unknown
    customers?: unknown
  } | null
}

/**
 * The stop types that end a leg by unloading, and so bound the window below.
 *
 * `depot_injection` alone. A `direct_injection` is the shipper bringing their own load to our
 * depot instead of us collecting it, so it is volume arriving, not volume leaving: see
 * LOADING_STOP_TYPES.
 */
export const UNLOAD_STOP_TYPES = new Set(["depot_injection"])

/**
 * The stop types that bring volume in, and so appear on the card.
 *
 * `direct_injection` is the shipper's own load delivered to the depot. It carries the shipper's
 * forecast exactly as a collection does -- the sync matches it through the location's customers
 * -- so the yard is told about it the same way.
 */
export const LOADING_STOP_TYPES = new Set([
  "shipper_collection",
  "haulage_collection",
  "direct_injection",
])

/**
 * The stop whose arrival the board announces.
 *
 * The depot injection where the route has one. A route without one is a direct injection, where
 * the shipper's vehicle is the route and its delivery to us is the arrival -- the last one, if a
 * single vehicle somehow brings more than one shipper's load.
 */
export function arrivalStop<T extends Pick<CollectionStopInput, "stop_type">>(
  stops: readonly T[]
): T | undefined {
  return (
    stops.find((stop) => stop.stop_type === "depot_injection") ??
    [...stops].reverse().find((stop) => stop.stop_type === "direct_injection")
  )
}

/**
 * Singular and plural for each loading unit type.
 *
 * Both pallet types collapse to "pallet". That is what the manual messages say and what the yard
 * says out loud, and a line reading "1 pallets (uk)" — which is what lowercasing the enum gives
 * you, and what the desktop column does — is the thing this table exists to avoid. The exact type
 * is not lost: it stays on `unitType` for anything that needs to tell EUR from UK.
 */
const UNIT_WORDS: Record<string, readonly [string, string]> = {
  "Pallets (UK)": ["pallet", "pallets"],
  "Pallets (EUR)": ["pallet", "pallets"],
  Cages: ["cage", "cages"],
  Magnums: ["magnum", "magnums"],
  "Sort Bags": ["sort bag", "sort bags"],
  "Loose Load": ["loose load", "loose loads"],
}

/** For a quantity with no unit type recorded against it. Better than naming the wrong noun. */
const UNKNOWN_UNIT_WORDS: readonly [string, string] = ["unit", "units"]

const PARCEL_WORDS: readonly [string, string] = ["parcel", "parcels"]

/** The unit named for the quantity it counts: "1 cage", "3 pallets". */
export function unitWord(unitType: string | null | undefined, quantity: number): string {
  const words = (unitType && UNIT_WORDS[unitType]) || UNKNOWN_UNIT_WORDS
  return quantity === 1 ? words[0] : words[1]
}

/** What a figure on a shipper's line is counting. */
export type CollectionMeasure = "loading_units" | "parcels" | "none"

export interface TurnaroundCollection {
  stopInstanceId: string
  /** The customers at the stop, or the stop's own name where none is linked. Never blank. */
  name: string
  /**
   * The figure to show, or null when nothing has been recorded at all.
   *
   * Loading units where there are any, because that is what comes off a tail lift and what the
   * manual messages counted. A parcel count is the fallback rather than an equal: it answers "how
   * much work", not "how much space", and cannot be checked off against a cage.
   */
  quantity: number | null
  measure: CollectionMeasure
  /** Pluralised for the quantity — "cage", "pallets". Null when there is no quantity. */
  unit: string | null
  /** The raw loading_unit_type, unmapped, for anything that needs EUR told from UK. */
  unitType: string | null
  /**
   * True where the figure on this line is a count somebody confirmed rather than the plan's.
   *
   * Asked of whichever measure won, which is why it is not `reportedStopVolume`'s `isActual`.
   * That one describes the *parcel* figure alone, so a stop whose loading units had been counted
   * but whose parcels had not came back as a forecast — and a card showing "3 pallets" would have
   * called a confirmed count a guess.
   */
  isActual: boolean
  /** Parcels, always, so a card can show both without re-deriving the rule. */
  volume: number
}

const asRecordArray = (value: unknown): Array<Record<string, unknown>> =>
  Array.isArray(value) ? (value as Array<Record<string, unknown>>) : []

/**
 * What to call this shipper.
 *
 * The customer names linked to the location, joined, because one site can serve several — and
 * failing that the stop's own name.
 *
 * That fallback is the behaviour change here. The names column dropped a stop with no customer
 * linkage entirely, which is defensible in a column headed "Customers" and is not defensible on a
 * card that claims to list what is on the van: a five-collection route showed four lines and
 * nothing said one was missing. A stop always has a name, so there is always something truthful to
 * print.
 */
export function collectionName(stop: CollectionStopInput): string {
  const location = stop.location
  const linked = asRecordArray(location?.location_customers ?? location?.customers)
  const names = linked
    .map((entry) => (entry?.customer as { name?: string } | undefined)?.name)
    .filter((name): name is string => !!name && name.trim().length > 0)

  if (names.length > 0) return names.join(", ")
  return stop.name
}

/**
 * One shipper's line: what they are called, and what they put on.
 *
 * The actual-beats-forecast rule is `reportedStopVolume`'s, not a second copy of it, so a card and
 * the stop sheet cannot disagree about a stop. What is added on top is the choice between units and
 * parcels, and the asymmetry in how a zero is read: a confirmed zero is a fact worth printing —
 * somebody went and there was nothing — while `loading_units_forecast` and `volume_forecast` both
 * default to 0 in the schema, so a zero forecast is silence and falls through.
 */
export function toCollection(stop: CollectionStopInput): TurnaroundCollection {
  const reported = reportedStopVolume({
    volumeForecast: stop.volume_forecast,
    volumeActual: stop.volume_actual,
    loadingUnitsForecast: stop.loading_units_forecast,
    loadingUnitsActual: stop.loading_units_actual,
    loadingUnitType: stop.loading_unit_type,
  })

  const base = {
    stopInstanceId: stop.id,
    name: collectionName(stop),
    unitType: stop.loading_unit_type ?? null,
    volume: reported.volume,
  }

  const hasUnits =
    stop.loading_units_actual != null ||
    (stop.loading_units_forecast != null && stop.loading_units_forecast > 0)

  if (hasUnits) {
    const quantity = reported.loadingUnits ?? 0
    return {
      ...base,
      quantity,
      measure: "loading_units",
      unit: unitWord(stop.loading_unit_type, quantity),
      isActual: stop.loading_units_actual != null,
    }
  }

  const hasParcels = stop.volume_actual != null || (stop.volume_forecast ?? 0) > 0
  if (hasParcels) {
    const quantity = reported.volume
    return {
      ...base,
      quantity,
      measure: "parcels",
      unit: quantity === 1 ? PARCEL_WORDS[0] : PARCEL_WORDS[1],
      isActual: reported.isActual,
    }
  }

  return { ...base, quantity: null, measure: "none", unit: null, isActual: false }
}

/**
 * The collections this injection is carrying.
 *
 * The window is the stops between the previous unload and this one, which is the rule the names
 * column already used and the reason it exists: a run that drops at a spoke and carries on
 * collecting would otherwise announce its second drop with the first drop's cages still listed.
 *
 * A stop Samsara marked `skipped` is left out. Nothing came off it, so listing it would have the
 * yard expecting cages that were never loaded — and unlike the read-side views, there is no room
 * on a card to show a line and then explain that it does not count.
 *
 * A direct injection that is itself the arrival is listed too: the load it announces is its own.
 * A depot injection is not a loading type, so including the injection in the slice costs nothing.
 *
 * `stops` must be in sequence order. Every test here is positional, and the board's query already
 * returns them ordered.
 */
export function collectionsForInjection(
  stops: readonly CollectionStopInput[],
  injectionStopId: string
): TurnaroundCollection[] {
  const injectionIndex = stops.findIndex((stop) => stop.id === injectionStopId)
  if (injectionIndex < 0) return []

  let windowStart = 0
  for (let i = injectionIndex - 1; i >= 0; i--) {
    if (UNLOAD_STOP_TYPES.has(stops[i].stop_type)) {
      windowStart = i + 1
      break
    }
  }

  return stops
    .slice(windowStart, injectionIndex + 1)
    .filter((stop) => LOADING_STOP_TYPES.has(stop.stop_type) && stop.status !== "skipped")
    .map(toCollection)
}

/**
 * A shipper's figure as the card prints it.
 *
 * Three shapes, because "0 pallets" and "0 parcels" both read as a fault in the board rather than
 * as a fact about the load:
 *
 *   - nothing recorded          -> "not recorded"
 *   - a confirmed count of zero -> "nothing collected"
 *   - anything else             -> "3 pallets"
 *
 * A zero that is *not* confirmed cannot arise from `toCollection` -- a zero forecast falls through
 * to `quantity: null` -- but it is handled rather than trusted, because a caller building a
 * collection by hand should not be able to produce "0 pallets" through this function.
 *
 * The forecast marker is deliberately not part of the string. It is drawn separately so it can be
 * drawn quietly, and so it only ever appears on an unconfirmed line -- which makes an unmarked
 * line mean "this count is real", a rule the eye learns after two cards.
 */
export function collectionFigure(collection: TurnaroundCollection): string {
  if (collection.quantity === null) return "not recorded"
  if (collection.quantity === 0) return collection.isActual ? "nothing collected" : "not recorded"
  return `${collection.quantity.toLocaleString()} ${collection.unit}`
}

